import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface ErrorBoundaryProps {
  readonly children: ReactNode
}

interface ErrorBoundaryState {
  readonly error: Error | undefined
}

/**
 * Top-level error boundary (error kit — UID-27's "build it into this
 * foundation" list). Wraps the whole app (see App.tsx) so an unhandled
 * render error in any screen shows a recoverable failed-load state instead
 * of a blank white page. Deliberately generic/reusable, not per-endpoint —
 * per-request failures (a 404/500 from a specific API call) are each
 * screen's own concern, using `<EmptyState>` for that; this boundary is the
 * last-resort catch for a render-time exception.
 *
 * Class component because `getDerivedStateFromError`/`componentDidCatch`
 * have no hook equivalent yet — this is the one place in the codebase a
 * class component is the correct tool.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: undefined }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Deliberately console-only for this foundation pass — a real error-
    // reporting sink (Sentry/CloudWatch RUM) is out of scope here; wiring
    // one up is a follow-up, not a silent gap (flag to devops/architect).
    console.error('Unhandled render error caught by <ErrorBoundary>', error, info.componentStack)
  }

  private handleReload = (): void => {
    window.location.reload()
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="error-boundary-panel" role="alert">
          <h1 className="error-boundary-title">Something went wrong</h1>
          <p className="error-boundary-detail">
            An unexpected error occurred. Reloading the page usually fixes this.
          </p>
          <button type="button" className="btn btn-primary btn-md" onClick={this.handleReload}>
            Reload page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
